import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useSwiper, useSwiperSlide } from 'swiper/react';
import styles from './carousel-slide-content.module.scss';

export interface CarouselSlideContentProps {
  /** The content of the slide */
  children: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /**
   * Whether slides out of view are taken out of the tab order and hidden from
   * screen readers, for slideshows. Turn off for strips of items that should
   * all be reachable with Tab
   */
  hideWhenOutOfView?: boolean;
}

/**
 * Keyboard behaviour for the content of a carousel slide, placed inside a
 * SwiperSlide. The Swiper needs `watchSlidesProgress` to know what is in view.
 * - Slides fully out of view are inert (when hideWhenOutOfView is on)
 * - A slide that is only partly in view slides into view when it gets focus
 */
const CarouselSlideContent = ({
  children,
  className,
  hideWhenOutOfView = true,
}: CarouselSlideContentProps) => {
  const swiper = useSwiper();
  const { isVisible } = useSwiperSlide();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const content = contentRef.current;

    if (!content || !swiper) return;

    const handleFocusIn = () => {
      const slide = content.closest<HTMLElement>('.swiper-slide');

      if (!slide || !swiper.el) return;

      const containerRect = swiper.el.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const isFullyVisible =
        slideRect.left >= containerRect.left - 1 &&
        slideRect.right <= containerRect.right + 1;

      if (isFullyVisible) return;

      if (swiper.params.loop) {
        swiper.slideToLoop(Number(slide.dataset.swiperSlideIndex));
      } else {
        swiper.slideTo(swiper.slides.indexOf(slide));
      }
    };

    content.addEventListener('focusin', handleFocusIn);

    return () => {
      content.removeEventListener('focusin', handleFocusIn);
    };
  }, [swiper]);

  return (
    <div
      ref={contentRef}
      // Spread, as the React 18 types don't know inert yet
      {...(hideWhenOutOfView && !isVisible && { inert: true })}
      className={classNames(styles.component, className)}
    >
      {children}
    </div>
  );
};

CarouselSlideContent.displayName = 'CarouselSlideContent';

export default CarouselSlideContent;
